import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Video, FileText, PencilRuler, HelpCircle, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

export const LESSON_TYPE_META = {
  video: { label: "Video", iconBg: "bg-red-50 text-red-600" },
  document: { label: "Presentación / Documento", iconBg: "bg-blue-50 text-blue-600" },
  practice: { label: "Práctica", iconBg: "bg-amber-50 text-amber-600" },
  quiz: { label: "Quiz", iconBg: "bg-violet-50 text-violet-600" },
};

const uid = () => Math.random().toString(36).slice(2, 10);

const emptyQuestion = (type = "single") => ({
  id: uid(),
  type,
  prompt: "",
  options: type === "fill_blank" ? [] : [{ id: uid(), text: "" }, { id: uid(), text: "" }],
  correct_option_ids: [],
  blanks: type === "fill_blank" ? [{ id: uid(), answer: "", alternatives: [] }] : [],
  points: 1,
});

export default function LessonDialog({ open, moduleId, lesson, onClose, onSaved }) {
  const isEdit = !!lesson;
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({
    type: lesson?.type || "video",
    title: lesson?.title || "",
    is_free_preview: lesson?.is_free_preview || false,
    duration_minutes: lesson?.duration_minutes ?? "",
    youtube_url: lesson?.youtube_url || "",
    doc_url: lesson?.doc_url || "",
    doc_file_id: lesson?.doc_file_id || null,
    doc_filename: null,
    body: lesson?.body || "",
    attachment_file_id: lesson?.attachment_file_id || null,
    attachment_filename: null,
    quiz_pass_pct: lesson?.quiz_pass_pct ?? 70,
    quiz_max_attempts: lesson?.quiz_max_attempts ?? 0,
    quiz_questions: lesson?.quiz_questions?.length ? lesson.quiz_questions : [],
  });

  const uploadAsset = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/courses/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, [field]: r.data.file_id, [`${field.replace("_file_id", "")}_filename`]: r.data.filename }));
      toast.success("Archivo subido");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error subiendo archivo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Poné un título a la lección"); return; }
    if (f.type === "video" && !f.youtube_url.trim()) { toast.error("Pegá el link de YouTube"); return; }
    if (f.type === "document" && !f.doc_url.trim() && !f.doc_file_id) { toast.error("Subí un PDF o pegá un link"); return; }
    if (f.type === "quiz" && f.quiz_questions.length === 0) { toast.error("Agregá al menos una pregunta"); return; }
    setSaving(true);
    const payload = {
      title: f.title,
      is_free_preview: f.is_free_preview,
      duration_minutes: f.duration_minutes === "" ? null : Number(f.duration_minutes),
      youtube_url: f.youtube_url || null,
      doc_url: f.doc_url || null,
      doc_file_id: f.doc_file_id,
      body: f.body || null,
      attachment_file_id: f.attachment_file_id,
      quiz_pass_pct: Number(f.quiz_pass_pct) || 70,
      quiz_max_attempts: Number(f.quiz_max_attempts) || 0,
      quiz_questions: f.quiz_questions,
    };
    try {
      if (isEdit) {
        await api.patch(`/course-lessons/${lesson.id}`, payload);
      } else {
        await api.post(`/course-modules/${moduleId}/lessons`, { type: f.type, ...payload });
      }
      toast.success(isEdit ? "Lección guardada" : "Lección agregada");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando la lección");
    } finally {
      setSaving(false);
    }
  };

  // ---- Quiz question helpers ----
  const updateQ = (qi, patch) => setF((s) => ({
    ...s,
    quiz_questions: s.quiz_questions.map((q, i) => (i === qi ? { ...q, ...patch } : q)),
  }));

  const setQType = (qi, type) => {
    const q = f.quiz_questions[qi];
    updateQ(qi, {
      type,
      options: type === "fill_blank" ? [] : (q.options?.length ? q.options : [{ id: uid(), text: "" }, { id: uid(), text: "" }]),
      correct_option_ids: type === "fill_blank" ? [] : (type === "single" ? q.correct_option_ids.slice(0, 1) : q.correct_option_ids),
      blanks: type === "fill_blank" ? (q.blanks?.length ? q.blanks : [{ id: uid(), answer: "", alternatives: [] }]) : [],
    });
  };

  const toggleCorrect = (qi, optId) => {
    const q = f.quiz_questions[qi];
    let ids;
    if (q.type === "single") ids = [optId];
    else ids = q.correct_option_ids.includes(optId) ? q.correct_option_ids.filter((x) => x !== optId) : [...q.correct_option_ids, optId];
    updateQ(qi, { correct_option_ids: ids });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="lesson-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Editar lección" : "Nueva lección"}</DialogTitle></DialogHeader>

        {/* Type picker (create only) */}
        {!isEdit && (
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(LESSON_TYPE_META).map(([type, meta]) => {
              const Icon = { video: Video, document: FileText, practice: PencilRuler, quiz: HelpCircle }[type];
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setF({ ...f, type })}
                  className={`p-3 rounded-lg border text-center transition-colors ${f.type === type ? "border-brand-blue bg-brand-blue/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`lesson-type-${type}`}
                >
                  <span className={`inline-flex p-2 rounded-md ${meta.iconBg}`}><Icon className="h-4 w-4" /></span>
                  <div className="text-[11px] font-medium text-slate-700 mt-1">{meta.label}</div>
                </button>
              );
            })}
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Título *</Label>
              <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Ej: Los saludos en español" data-testid="lesson-title-input" />
            </div>
            <div>
              <Label className="text-xs">Duración (min)</Label>
              <Input type="number" min="0" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} data-testid="lesson-duration-input" />
            </div>
          </div>

          {f.type === "video" && (
            <div>
              <Label className="text-xs">Link de YouTube *</Label>
              <Input value={f.youtube_url} onChange={(e) => setF({ ...f, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." data-testid="lesson-youtube-input" />
              <p className="text-[11px] text-slate-400 mt-1">Subí el video a YouTube como "No listado" y pegá el link aquí. Se reproduce incrustado sin mostrar el enlace.</p>
            </div>
          )}

          {f.type === "document" && (
            <div className="space-y-2">
              <Label className="text-xs">PDF o link externo *</Label>
              <div className="flex gap-2 items-center">
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="lesson-doc-upload-btn">
                  <Upload className="h-3.5 w-3.5 mr-1" /> {uploading ? "Subiendo…" : "Subir PDF"}
                </Button>
                {f.doc_file_id && <span className="text-xs text-emerald-700">✓ {f.doc_filename || "Archivo subido"}</span>}
              </div>
              <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadAsset(e, "doc_file_id")} />
              <Input value={f.doc_url} onChange={(e) => setF({ ...f, doc_url: e.target.value })} placeholder="…o pegá un link (Google Slides, Canva, etc.)" data-testid="lesson-doc-url-input" />
            </div>
          )}

          {f.type === "practice" && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Instrucciones / contenido *</Label>
                <Textarea rows={5} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Explicá la práctica: qué debe hacer el estudiante, ejemplos, etc." data-testid="lesson-body-input" />
              </div>
              <div className="flex gap-2 items-center">
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="lesson-attachment-btn">
                  <Upload className="h-3.5 w-3.5 mr-1" /> {uploading ? "Subiendo…" : "Adjuntar material (PDF/imagen)"}
                </Button>
                {f.attachment_file_id && <span className="text-xs text-emerald-700">✓ {f.attachment_filename || "Adjunto listo"}</span>}
              </div>
              <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadAsset(e, "attachment_file_id")} />
            </div>
          )}

          {f.type === "quiz" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Nota mínima para aprobar (%)</Label>
                  <Input type="number" min="0" max="100" value={f.quiz_pass_pct} onChange={(e) => setF({ ...f, quiz_pass_pct: e.target.value })} data-testid="quiz-pass-pct" />
                </div>
                <div>
                  <Label className="text-xs">Intentos máximos (0 = ilimitados)</Label>
                  <Input type="number" min="0" value={f.quiz_max_attempts} onChange={(e) => setF({ ...f, quiz_max_attempts: e.target.value })} data-testid="quiz-max-attempts" />
                </div>
              </div>

              {f.quiz_questions.map((q, qi) => (
                <div key={q.id} className="p-3 rounded-lg border border-slate-200 space-y-2 bg-slate-50/50" data-testid={`quiz-question-${qi}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-500">P{qi + 1}</span>
                    <Select value={q.type} onValueChange={(v) => setQType(qi, v)}>
                      <SelectTrigger className="h-8 w-44 text-xs" data-testid={`qtype-${qi}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Selección única</SelectItem>
                        <SelectItem value="multiple">Selección múltiple</SelectItem>
                        <SelectItem value="fill_blank">Completar espacios</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex-1" />
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setF((s) => ({ ...s, quiz_questions: s.quiz_questions.filter((_, i) => i !== qi) }))} data-testid={`qdel-${qi}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Textarea
                    rows={2}
                    value={q.prompt}
                    onChange={(e) => updateQ(qi, { prompt: e.target.value })}
                    placeholder={q.type === "fill_blank" ? 'Usá ___ donde va cada espacio. Ej: "Yo ___ de Costa Rica."' : "Enunciado de la pregunta"}
                    data-testid={`qprompt-${qi}`}
                  />
                  {q.type !== "fill_blank" ? (
                    <div className="space-y-1.5">
                      {q.options.map((o, oi) => (
                        <div key={o.id} className="flex items-center gap-2">
                          <Checkbox checked={q.correct_option_ids.includes(o.id)} onCheckedChange={() => toggleCorrect(qi, o.id)} data-testid={`qopt-correct-${qi}-${oi}`} />
                          <Input className="h-8 text-sm" value={o.text} onChange={(e) => updateQ(qi, { options: q.options.map((x, i) => (i === oi ? { ...x, text: e.target.value } : x)) })} placeholder={`Opción ${oi + 1}`} data-testid={`qopt-text-${qi}-${oi}`} />
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400" onClick={() => updateQ(qi, { options: q.options.filter((_, i) => i !== oi), correct_option_ids: q.correct_option_ids.filter((x) => x !== o.id) })}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      <Button size="sm" variant="ghost" className="text-xs text-brand-blue h-7" onClick={() => updateQ(qi, { options: [...q.options, { id: uid(), text: "" }] })} data-testid={`qopt-add-${qi}`}>
                        <Plus className="h-3 w-3 mr-1" /> Opción
                      </Button>
                      <p className="text-[10px] text-slate-400">Marcá el checkbox de la(s) respuesta(s) correcta(s).</p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {q.blanks.map((b, bi) => (
                        <div key={b.id} className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 w-16">Espacio {bi + 1}</span>
                          <Input className="h-8 text-sm" value={b.answer} onChange={(e) => updateQ(qi, { blanks: q.blanks.map((x, i) => (i === bi ? { ...x, answer: e.target.value } : x)) })} placeholder="Respuesta correcta" data-testid={`qblank-answer-${qi}-${bi}`} />
                          <Input className="h-8 text-sm" value={(b.alternatives || []).join(", ")} onChange={(e) => updateQ(qi, { blanks: q.blanks.map((x, i) => (i === bi ? { ...x, alternatives: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } : x)) })} placeholder="Alternativas aceptadas (coma)" />
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400" onClick={() => updateQ(qi, { blanks: q.blanks.filter((_, i) => i !== bi) })}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      <Button size="sm" variant="ghost" className="text-xs text-brand-blue h-7" onClick={() => updateQ(qi, { blanks: [...q.blanks, { id: uid(), answer: "", alternatives: [] }] })} data-testid={`qblank-add-${qi}`}>
                        <Plus className="h-3 w-3 mr-1" /> Espacio
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setF((s) => ({ ...s, quiz_questions: [...s.quiz_questions, emptyQuestion()] }))} data-testid="quiz-add-question">
                <Plus className="h-3.5 w-3.5 mr-1" /> Agregar pregunta
              </Button>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pt-1">
            <Checkbox checked={f.is_free_preview} onCheckedChange={(v) => setF({ ...f, is_free_preview: !!v })} data-testid="lesson-free-preview" />
            Lección de muestra gratis (visible en la página de venta sin comprar)
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="btn-brand" onClick={save} disabled={saving || uploading} data-testid="lesson-save-btn">
            {saving ? "Guardando…" : "Guardar lección"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
